import { Component, OnInit, OnChanges, SimpleChanges, Output, EventEmitter, Input } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { LegacyReplayTemplateService } from '../../services/simulator/legacy-replay-template.service';
import { LegacyReplayTemplate } from '../../models/simulator.models';

@Component({
  selector: 'app-legacy-replay-template-selector',
  standalone: true,
  imports: [FormsModule],
  template: `
    <div class="template-selector">
      <div class="form-group">
        <label for="templateSelect" class="form-label">
          Legacy Tournament Template
          <i
            class="fa fa-info-circle ms-1"
            title="Select a tournament to auto-populate database credentials"
          ></i>
        </label>
        <select
          id="templateSelect"
          class="form-control"
          [(ngModel)]="selectedTemplateId"
          (change)="onTemplateSelected()"
          [disabled]="isLoading"
          >
          <option value="">-- Select a tournament --</option>
          @for (template of templates; track template) {
            <option
              [value]="template.id"
              [title]="template.description || template.databaseName"
              >
              {{ template.templateName }}
            </option>
          }
        </select>
        <small class="form-text text-muted mt-1">
          Template will auto-fill database name, server, and tournament ID
        </small>
      </div>
    
      @if (isLoading) {
        <div class="loading">
          <span class="spinner-border spinner-border-sm me-2"></span>
          Loading templates...
        </div>
      }
    
      @if (error) {
        <div class="alert alert-danger">
          {{ error }}
        </div>
      }
    </div>
    `,
  styles: [
    `
      .template-selector {
        padding: 12px 0;
      }

      .form-group {
        margin-bottom: 16px;
      }

      .form-label {
        display: block;
        margin-bottom: 8px;
        font-weight: 600;
        font-size: 14px;
      }

      .form-text {
        display: block;
        margin-top: 4px;
      }

      .text-muted {
        color: #6c757d;
        font-size: 12px;
      }

      .ms-1 {
        margin-left: 4px;
      }

      .mt-1 {
        margin-top: 4px;
      }

      .me-2 {
        margin-right: 8px;
      }

      .fa-info-circle {
        color: #17a2b8;
        cursor: help;
      }

      /* INSPINIA-style select sizing to match inputs */
      select.form-control {
        width: 100%;
        font-size: 14px;
        height: 34px;
        padding: 6px 12px;
        line-height: 20px; /* avoid clipped text */
      }
      select.form-control:focus {
        outline: none;
        border-color: #0d6efd;
        box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.25);
      }

      .template-details {
        margin-top: 16px;
        padding: 12px;
        background-color: #f8f9fa;
        border-radius: 4px;
        border-left: 4px solid #0d6efd;
      }

      .detail-row {
        display: flex;
        margin-bottom: 8px;
        gap: 12px;
      }

      .detail-row:last-child {
        margin-bottom: 0;
      }

      .label {
        font-weight: 600;
        min-width: 120px;
        color: #495057;
      }

      .value {
        flex: 1;
        color: #212529;
        word-break: break-word;
      }

      .badge-success {
        background-color: #28a745;
        color: white;
        padding: 4px 8px;
        border-radius: 3px;
        font-size: 12px;
      }

      .loading {
        margin-top: 12px;
        color: #6c757d;
        font-size: 14px;
      }

      .spinner-border {
        display: inline-block;
        width: 1rem;
        height: 1rem;
        vertical-align: text-bottom;
        border: 0.25em solid currentColor;
        border-right-color: transparent;
        border-radius: 50%;
        animation: spinner-border 0.75s linear infinite;
      }

      .spinner-border-sm {
        width: 0.875rem;
        height: 0.875rem;
        border-width: 0.2em;
      }

      @keyframes spinner-border {
        to {
          transform: rotate(360deg);
        }
      }

      .alert {
        padding: 12px;
        border-radius: 4px;
        margin-top: 12px;
      }

      .alert-danger {
        background-color: #f8d7da;
        border: 1px solid #f5c6cb;
        color: #721c24;
      }
    `,
  ],
})
export class LegacyReplayTemplateSelectorComponent implements OnInit, OnChanges {
  @Input() selectedTemplateId: string | null = null;
  @Output() templateSelected = new EventEmitter<LegacyReplayTemplate | null>();

  templates: LegacyReplayTemplate[] = [];
  selectedTemplate: LegacyReplayTemplate | null = null;
  isLoading = false;
  error: string | null = null;

  constructor(
    private legacyReplayTemplateService: LegacyReplayTemplateService
  ) {}

  ngOnInit(): void {
    this.loadTemplates();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Handle selectedTemplateId changes after templates are already loaded
    if (changes['selectedTemplateId'] && this.templates.length > 0) {
      const newId = changes['selectedTemplateId'].currentValue;
      if (newId) {
        const template = this.templates.find((t) => t.id === newId);
        if (template) {
          this.selectedTemplate = template;
        }
      } else {
        this.selectedTemplate = null;
      }
    }
  }

  private loadTemplates(): void {
    this.isLoading = true;
    this.error = null;

    this.legacyReplayTemplateService.getActiveTemplates().subscribe({
      next: (templates) => {
        this.templates = templates;
        this.isLoading = false;

        // If a template was pre-selected, find and set it
        if (this.selectedTemplateId && this.templates.length > 0) {
          const template = this.templates.find(
            (t) => t.id === this.selectedTemplateId
          );
          if (template) {
            this.selectedTemplate = template;
          }
        }
      },
      error: (err) => {
        this.error =
          'Failed to load Legacy Replay templates. Please try again.';
        this.isLoading = false;
        console.error('Error loading templates:', err);
      },
    });
  }

  onTemplateSelected(): void {
    if (this.selectedTemplateId) {
      const template = this.templates.find(
        (t) => t.id === this.selectedTemplateId
      );
      if (template) {
        this.selectedTemplate = template;
        this.templateSelected.emit(template);
      }
    } else {
      // User cleared selection - emit null
      this.selectedTemplate = null;
      this.templateSelected.emit(null);
    }
  }
}
